import { addVehicle } from "action/garage/addVehicle";
import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import classNames from "classnames";
import PAButton, { BUTTON_SIZE, BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import BoxedVehicleSelector from "component/vehicle-form/BoxedVehicleSelector";
import matchMediaConst from "constant/matchmedia";
import { useCustomScreenSize } from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { IVehicle } from "interfaces/session/IVehicle";
import { CUSTOM_SIZES_WARNINGS } from "modules/category/constants/customSizesConstants";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getValidCategory } from "util/category/get-valid-category";
import { isWheels } from "util/category/is-wheels";
import { toLowerCase } from "util/lodash/toLowerCase";
import {
  PRODUCT_PAGE_TYPES,
} from "util/product/product-constants";
import { refreshData } from "util/refreshData";
import { getVehicleSelectionOrder } from "util/vehicle/get-vehicle-selection-order";
import getFocusEl from "util/vehicle/getFocusEl";
import { getVehicleName } from "../../util/helper/getVehicleName";
import FitmentNotesContainer from "../fitment-notes/FitmentNotesContainer";
import FitmentOptions from "./FitmentOptions";
import styles from "./product-fitment.module.scss";

const AutoSyncVehicle = dynamic(() => import(/* webpackChunkName: "auto-sync-vehicle" */ "modules/product/auto-sync/AutoSyncVehicle"));

interface IProductFitmentBox {
  product: ICompactProduct,
  fitment: "ERROR" | "SUCCESS" | "SELECT_VEHICLE_WARNING" | "SELECT_FITMENT_WARNING";
  nextAttribute?: string;
  className?: string;
  iconSize?: string;
  fromSideMenu?: boolean;
  formView?: string;
  showBorder?: boolean;
  isCompactFitment?: boolean;
  isLongErrMsg: boolean;
}

function ProductFitmentBox(props: IProductFitmentBox) {
  const {
    product, fitment, className = "", iconSize, defaultVehicle,
    fromSideMenu, formView, nextAttribute,
    showBorder = false, isCompactFitment = false,
    fitmentOptionsDetails, attributeTitle, validateFitment, showFitmentVehicleSelector,
    showMore, hideFitment, isLongErrMsg, isHorizontalVehicleSelector,
  } = props;

  const dispatch = useDispatch();

  const { fitmentNote, requireFullVehicleSelection, fitmentStatus } = product;

  const { selectedVehicle } = useSelector((store: RootState) => store.garage);
  const { currentPageType } = useSelector((store: RootState) => store.utils);

  const updatedDefaultVehicle = {
    defaultMake: defaultVehicle.defaultMake || selectedVehicle?.make,
    defaultModel: defaultVehicle.defaultModel || selectedVehicle?.model,
    defaultYear: defaultVehicle.defaultYear || selectedVehicle?.year,
  };

  const selectionOrder = getVehicleSelectionOrder(updatedDefaultVehicle.defaultMake,
    updatedDefaultVehicle.defaultModel, updatedDefaultVehicle.defaultYear);
  const focusElement = getFocusEl(selectionOrder, defaultVehicle);
  const [focusedFitmentName, setFocusedFitmentName] = useState(focusElement);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);

  const { t } = useTranslation(["product", "category"]);
  const router = useRouter();
  const { pathname } = router.query;
  const isMobile = useCustomScreenSize({
    maxWidth: matchMediaConst.XS_MAX,
  });

  const isCustomSizeWarning = CUSTOM_SIZES_WARNINGS.includes(fitmentStatus);
  const isFitmentError = fitment === "ERROR";
  const isFitmentSuccess = fitment === "SUCCESS";
  const isSelectVehicleWarning = fitment === "SELECT_VEHICLE_WARNING";
  const isSelectFitmentWarning = fitment === "SELECT_FITMENT_WARNING" || isCustomSizeWarning;
  const isWarning = isSelectVehicleWarning || isSelectFitmentWarning;
  const vehicleName = getVehicleName(selectedVehicle, ["year", "make", "model"]);

  const updatedFormView = fromSideMenu ? "verticalView" : formView;
  const updatedExpanded = Boolean((router.asPath.split("#")[1] === "VEHICLEFITMENT" || !showMore));

  const categoryTitle = isWheels(toLowerCase(getValidCategory(product).tier1)) ? "wheel" : "tire";

  const customSizesWarning = `The custom ${categoryTitle} size may  not fit  the vehicle ${vehicleName}`;

  useEffect(() => {
    // After deselecting vehicle, focus on first input in selection order
    if (!selectedVehicle && !defaultVehicle.defaultMake && !defaultVehicle.defaultModel && !defaultVehicle.defaultYear) {
      setFocusedFitmentName(selectionOrder[0]);
    }
    if (!selectedVehicle && defaultVehicle.defaultMake && defaultVehicle.defaultModel) {
      setFocusedFitmentName(selectionOrder[2]);
    }
  }, [selectedVehicle]);

  useEffect(() => {
    setFocusedFitmentName(focusElement);
  }, [updatedDefaultVehicle.defaultMake, updatedDefaultVehicle.defaultModel, updatedDefaultVehicle.defaultYear]);

  function getFitmentHeading() {
    if (isCustomSizeWarning) {
      return customSizesWarning;
    }

    if (!nextAttribute) {
      return null;
    }
    return t("select_attribute", { attribute: attributeTitle || "" });
  }

  const fitmentHeading = getFitmentHeading();

  function selectVehicleSettings(vehicle: IVehicle) {
    return refreshData({
      pathname,
      isMobile,
      selectedVehicle: vehicle,
    });
  }

  const getFitmentErrorMsg = () => (isLongErrMsg ? `${t("This part does NOT fit")} ${vehicleName}` : t("This part does NOT fit your vehicle"));

  return (
    <>
      <div
        className={classNames(styles.product_fitment_box, styles.product_fitment_container, className, {
          [styles.error_container]: isFitmentError,
          [styles.success_container]: isFitmentSuccess,
          [styles.warning_container]: isCustomSizeWarning || isSelectFitmentWarning || (isSelectVehicleWarning && showBorder),
        }, className)}
        style={{ padding: !selectedVehicle && !showBorder ? "1rem 0rem" : undefined }}
      >
        <div className={`d-flex ${styles.product_not_fit}`}>
          <div className="flex-grow-1" style={{ width: "100%" }}>
            <div className={styles.icon_description_container}>
              <div>
                <div className={classNames(styles.fitment_icon, {
                  [styles.error_icon]: isFitmentError,
                  [styles.success_icon]: isFitmentSuccess,
                  [styles.warning_icon]: isWarning || isCustomSizeWarning,
                  [styles.big_icon]: (iconSize === "big"),
                  [styles.small_icon]: iconSize === "small",
                  [styles.small_warning_icon]: iconSize === "small" && isWarning,
                })}
                >
                  <i className={classNames("fa", {
                    "fa-remove": isFitmentError,
                    "fa-check": isFitmentSuccess,
                    "fa-exclamation-triangle": isWarning || isCustomSizeWarning,
                  })}
                  />
                </div>
              </div>

              <div className="d-flex flex-column" style={{ width: "100%" }}>
                <PAText
                  component="span"
                  fontSize={FONT_SIZE[2]}
                  className={classNames(styles.fitment_heading, {
                    [styles.error_heading]: isFitmentError,
                    [styles.success_heading]: isFitmentSuccess,
                    [styles.warning_heading]: isWarning,
                  })}
                  data-testid="product-fitment-text"
                >
                  {isFitmentError && getFitmentErrorMsg()}
                  {isFitmentSuccess && t("fits_vehicle", { title: `${vehicleName}` })}
                  {isSelectVehicleWarning && !isCompactFitment && (
                    <>
                      <PAText className="p-r-5" color="black">
                        {t("Select Vehicle")}
                      </PAText>
                      <PAText className={styles.focus_element} color="black" fontSize={FONT_SIZE[2]} bold>
                        {` '${t(focusedFitmentName)}' `}
                      </PAText>
                      <PAText className="p-r-5" color="black">
                        {t("To Guarantee Fit")}
                      </PAText>
                      <br className="visible-lg visible-md" />
                    </>
                  )}
                  <HideIf show={isSelectVehicleWarning && isCompactFitment}>
                    <PAText component="p" fontSize={FONT_SIZE[2]} className="m-b-5">{t("fitment_error")}</PAText>
                  </HideIf>

                  {(isCustomSizeWarning || isSelectFitmentWarning) && !isCompactFitment && fitmentHeading}
                  <HideIf show={isSelectFitmentWarning && isCompactFitment && nextAttribute}>
                    {t("select_compact_attribute", { attribute: attributeTitle || "" })}
                  </HideIf>
                </PAText>
                <div className="d-flex align-items-center">
                  <HideIf show={!isFitmentSuccess && !isLongErrMsg && !isCustomSizeWarning}>
                    <PAText bold>
                      {`${vehicleName}`}
                    </PAText>
                  </HideIf>
                  <HideIf show={isCustomSizeWarning}>
                    <PAText fontSize={FONT_SIZE[0]}>
                      {t(`To ensure accurate fit, please choose Factory ${categoryTitle} Size.`)}
                    </PAText>
                  </HideIf>

                </div>
                <HideIf show={(requireFullVehicleSelection && !isFitmentSuccess && !isSelectVehicleWarning) || (isCustomSizeWarning && PRODUCT_PAGE_TYPES.includes(currentPageType))}>
                  <div className="m-t-5 m-b-5">
                    <PAButton
                      className={`btn ${styles.confirm_vehicle_fitment}`}
                      size={BUTTON_SIZE.STANDARD}
                      buttonType={BUTTON_TYPE.OUTLINE}
                      onClick={() => dispatch(openModal("vehicleSelectionOptionsModal", { forceTrigger: true, requireFullVehicleSelection }))}
                      widthAuto
                    >
                      Confirm Vehicle Fitments
                    </PAButton>
                  </div>
                </HideIf>
              </div>

            </div>
            <HideIf show={!isCustomSizeWarning && ((!selectedVehicle && !isCompactFitment) || showFitmentVehicleSelector)}>
              <div className={`${styles.vehicle_selector_container} ${fromSideMenu ? styles.side_menu_vehicle_selector : ""}`}>
                <BoxedVehicleSelector
                  category={getValidCategory(product)}
                  autoFocus={!fromSideMenu && !PRODUCT_PAGE_TYPES.includes(currentPageType)}
                  focusEl={focusElement}
                  {...updatedDefaultVehicle}
                  selectionOrder={selectionOrder}
                  onSelectVehicle={(vehicle) => dispatch(addVehicle(vehicle))}
                  handleFOBlur={(vehicle) => {
                    dispatch(addVehicle(vehicle));
                  }}
                  getFocusedFitmentNameHandler={(getFocusedFitmentName: string, nextTitle: string) => setFocusedFitmentName(nextTitle || getFocusedFitmentName)}
                  formView={updatedFormView}
                  dropdownOpenFromRight
                  isHorizontalVehicleSelector={isHorizontalVehicleSelector}
                />
              </div>
            </HideIf>

            <HideIf show={!requireFullVehicleSelection || isFitmentSuccess}>
              <>
                <HideIf show={!isFitmentError && !hideFitment}>
                  <FitmentOptions
                    fitmentOptionsDetails={fitmentOptionsDetails}
                    product={product}
                    validateFitment={validateFitment}
                    selectVehicleSettings={selectVehicleSettings}
                    expanded={updatedExpanded}
                  />
                </HideIf>
                <HideIf show={!hideFitment}>
                  <FitmentNotesContainer
                    {...{
                      fitmentNote,
                    }}
                    expanded={updatedExpanded}
                    setIsContentOverflowing={setIsContentOverflowing}
                    isContentOverflowing={isContentOverflowing}
                  />

                  <HideIf show={!updatedExpanded && isMobile && showMore && !isFitmentError && isContentOverflowing}>
                    <Link
                      href={`${router.query.pathname}#VEHICLEFITMENT`}
                      className={styles.fitment_show_more}
                    >
                      {t("Show More")}
                    </Link>
                  </HideIf>
                </HideIf>
              </>
            </HideIf>

            <HideIf show={isSelectVehicleWarning && isCompactFitment}>
              <PAButton
                className={`btn p-0 ${styles.select_vehicle_btn}`}
                buttonType={BUTTON_TYPE.NORMAL}
                onClick={() => dispatch(openModal("vehicleSelector", { ...updatedDefaultVehicle, selectionOrder }))}
              >
                Select Your Vehicle
              </PAButton>
            </HideIf>
          </div>
        </div>
        <HideIf show={product?.viewOnVehicleAvailable}>
          <div className="d-flex justify-content-end">
            <AutoSyncVehicle product={product} />
          </div>
        </HideIf>
      </div>

    </>
  );
}

export default ProductFitmentBox;
